/**
 * Centralized, context-aware report dropdown option definitions.
 *
 * Each getter function returns only the options relevant to the page that calls it,
 * so no page accidentally displays options it cannot handle.
 *
 * IDs for options that already existed in reportConstants.ts or
 * teamReportingConstant.ts are re-exported here for backward compatibility.
 * New-option IDs are defined in this file only.
 */
import type { DropdownOption } from "../components/common/Dropdown";

// ─── Re-exported existing IDs ─────────────────────────────────────────────────

export {
  TEAM_SUMMARY_OPTION_ID,
  TEAM_SUMMARY_OPTION_VALUE,
  TEAM_DETAILS_OPTION_ID,
  TEAM_DETAILS_OPTION_VALUE,
  EXPORT_TEST_OF_CHANGE_ID,
  EXPORT_TEST_OF_CHANGE_VALUE,
  EXPORT_TEST_DATA_OPTION_ID,
  EXPORT_TEST_DATA_OPTION_VALUE,
  EXPORT_GL_DATA_OPTION_ID,
  EXPORT_GL_DATA_OPTION_VALUE,
  EXPORT_UBT_DETAILS_OPTION_ID,
  EXPORT_UBT_DETAILS_OPTION_VALUE,
  EXPORT_TOC_PIP_OPTION_ID,
  EXPORT_TOC_PIP_OPTION_VALUE,
} from "../utils/reportConstants";

export {
  EXPORT_MEMBERSHIP_COUNT_CSV_ID,
  EXPORT_MEMBERSHIP_COUNT_CSV_VALUE,
} from "../constants/teamReportingConstant";

// ─── New option constants ─────────────────────────────────────────────────────

/** Export Privilege Roles CSV — FindAndSelectUBT page */
export const EXPORT_PRIVILEGE_ROLES_OPTION_ID = "export_privilege_roles_csv";
export const EXPORT_PRIVILEGE_ROLES_OPTION_VALUE = "export_privilege_roles_csv";

/** Export Roles CSV — MyTeams page */
export const EXPORT_ROLES_OPTION_ID = "export_roles_csv";
export const EXPORT_ROLES_OPTION_VALUE = "export_roles_csv";

/** Export PIP History CSV — SearchUBTProjects, MyTeams, DetailsContainer */
export const EXPORT_PIP_HISTORY_OPTION_ID = "export_pip_history_csv";
export const EXPORT_PIP_HISTORY_OPTION_VALUE = "export_pip_history_csv";

/** Export PIP Details CSV — SearchUBTProjects, MyTeams, DetailsContainer */
export const EXPORT_PIP_DETAILS_OPTION_ID = "export_pip_details_csv";
export const EXPORT_PIP_DETAILS_OPTION_VALUE = "export_pip_details_csv";

/** Test Data Charts — DetailsContainer Project Reports dropdown */
export const TEST_DATA_CHARTS_OPTION_ID = "test_data_charts";
export const TEST_DATA_CHARTS_OPTION_VALUE = "test_data_charts";

/** Print Project Records — DetailsContainer Project Reports dropdown */
export const PRINT_PROJECT_RECORDS_OPTION_ID = "print_project_records";
export const PRINT_PROJECT_RECORDS_OPTION_VALUE = "print_project_records";

/** Print Test Of Change — DetailsContainer Project Reports dropdown */
export const PRINT_TEST_OF_CHANGE_OPTION_ID = "print_test_of_change";
export const PRINT_TEST_OF_CHANGE_OPTION_VALUE = "print_test_of_change";

/** Export Test Data CSV for a specific project — DetailsContainer Project Reports dropdown */
export const EXPORT_PROJECT_TEST_DATA_OPTION_ID = "export_project_test_data_csv";
export const EXPORT_PROJECT_TEST_DATA_OPTION_VALUE = "export_project_test_data_csv";

/**
 * Banner message shown when a user attempts to export Test Data for a project
 * that has no Test of Change records. Reusable across any context that guards
 * the export with a `testOfChangeTotalItems === 0` check.
 */
export const TOC_NO_DATA_BANNER_MESSAGE =
  "The project you selected doesn't have any test of change data added to it. The data can be added on 'Test Of Change' tab.";

// ─── Shared icon paths ────────────────────────────────────────────────────────

const PRINTER_ICON = "/assets/images/printer-outline.svg";
const DOWNLOAD_ICON = "/assets/images/download-outline.svg";

// ─── Individual option definitions ────────────────────────────────────────────

const TEAM_SUMMARY_OPTION: DropdownOption = {
  id: "team_summary",
  label: "Team Summary",
  value: "team_summary",
  iconPath: PRINTER_ICON,
};

const TEAM_DETAILS_OPTION: DropdownOption = {
  id: "team_details",
  label: "Team Details",
  value: "team_details",
  iconPath: PRINTER_ICON,
};

const EXPORT_GL_DATA_OPTION: DropdownOption = {
  id: "export_gl_data_csv",
  label: "Export GL Data (CSV)",
  value: "export_gl_data_csv",
  iconPath: PRINTER_ICON,
};

const EXPORT_MEMBERSHIP_COUNT_OPTION: DropdownOption = {
  id: "export_membership_count_csv",
  label: "Export Membership Count (CSV)",
  value: "export_membership_count_csv",
  iconPath: PRINTER_ICON,
};

const EXPORT_PRIVILEGE_ROLES_OPTION: DropdownOption = {
  id: EXPORT_PRIVILEGE_ROLES_OPTION_ID,
  label: "Export Privilege Roles (CSV)",
  value: EXPORT_PRIVILEGE_ROLES_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const EXPORT_UBT_DETAILS_OPTION: DropdownOption = {
  id: "export_ubt_details_csv",
  label: "Export UBT Details (CSV)",
  value: "export_ubt_details_csv",
  iconPath: PRINTER_ICON,
};

const EXPORT_ROLES_OPTION: DropdownOption = {
  id: EXPORT_ROLES_OPTION_ID,
  label: "Export Roles (CSV)",
  value: EXPORT_ROLES_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const EXPORT_PIP_HISTORY_OPTION: DropdownOption = {
  id: EXPORT_PIP_HISTORY_OPTION_ID,
  label: "Export PIP History (CSV)",
  value: EXPORT_PIP_HISTORY_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const EXPORT_PIP_DETAILS_OPTION: DropdownOption = {
  id: EXPORT_PIP_DETAILS_OPTION_ID,
  label: "Export PIP Details (CSV)",
  value: EXPORT_PIP_DETAILS_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const EXPORT_TEST_OF_CHANGE_OPTION: DropdownOption = {
  id: "export_test_of_change_csv",
  label: "Export Test of Change (CSV)",
  value: "export_test_of_change_csv",
  iconPath: PRINTER_ICON,
};

const EXPORT_TEST_DATA_OPTION: DropdownOption = {
  id: "export_test_data_csv",
  label: "Export Test Data (CSV)",
  value: "export_test_data_csv",
  iconPath: PRINTER_ICON,
};

const EXPORT_TOC_PIP_OPTION: DropdownOption = {
  id: "export_toc_pip_csv",
  label: "Export Test of Change (CSV)",
  value: "export_toc_pip_csv",
  iconPath: PRINTER_ICON,
};

const EXPORT_TEAMS_REPORTING_CSV_OPTION: DropdownOption = {
  id: "export_team_summary_csv",
  label: "Export Teams Reporting (CSV)",
  value: "export_team_summary_csv",
  iconPath: PRINTER_ICON,
};

const TEAMS_REPORTING_PDF_OPTION: DropdownOption = {
  id: "teams_reporting",
  label: "Teams Reporting",
  value: "teams_reporting",
  iconPath: PRINTER_ICON,
};

const P2P_DETAIL_REPORTING_CSV_OPTION: DropdownOption = {
  id: "p2P_detail_reporting_csv",
  label: "P2P Detail Reporting (CSV)",
  value: "p2P_detail_reporting_csv",
  iconPath: PRINTER_ICON,
};

const P2P_DETAIL_REPORTING_PDF_OPTION: DropdownOption = {
  id: "p2P_detail_reporting_pdf",
  label: "P2P Detail Reporting (PDF)",
  value: "p2P_detail_reporting_pdf",
  iconPath: DOWNLOAD_ICON,
};

const PRINT_PROJECT_RECORDS_OPTION: DropdownOption = {
  id: PRINT_PROJECT_RECORDS_OPTION_ID,
  label: "Project Records",
  value: PRINT_PROJECT_RECORDS_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const PRINT_TEST_OF_CHANGE_OPTION: DropdownOption = {
  id: PRINT_TEST_OF_CHANGE_OPTION_ID,
  label: "Test Of Change",
  value: PRINT_TEST_OF_CHANGE_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const EXPORT_PROJECT_TEST_DATA_OPTION: DropdownOption = {
  id: EXPORT_PROJECT_TEST_DATA_OPTION_ID,
  label: "Export Test Data (CSV)",
  value: EXPORT_PROJECT_TEST_DATA_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

const TEST_DATA_CHARTS_OPTION: DropdownOption = {
  id: TEST_DATA_CHARTS_OPTION_ID,
  label: "Test Data Charts",
  value: TEST_DATA_CHARTS_OPTION_VALUE,
  iconPath: PRINTER_ICON,
};

// ─── Context-aware getter functions ──────────────────────────────────────────

/**
 * Returns dropdown options for the Find And Select UBT page.
 * Includes GL data, membership count, privilege roles, and UBT details exports.
 *
 * @returns Options array for the Find And Select UBT report dropdown.
 */
export const getFindAndSelectUBTOptions = (): DropdownOption[] => [
  TEAM_SUMMARY_OPTION,
  TEAM_DETAILS_OPTION,
  EXPORT_GL_DATA_OPTION,
  EXPORT_MEMBERSHIP_COUNT_OPTION,
  EXPORT_PRIVILEGE_ROLES_OPTION,
  EXPORT_UBT_DETAILS_OPTION,
];

/**
 * Returns dropdown options for the Teams Reporting page.
 * Shows PDF and CSV exports relevant to the team-level reporting search results.
 *
 * @returns Options array for the Teams Reporting report dropdown.
 */
export const getTeamsReportingOptions = (): DropdownOption[] => [
  EXPORT_TEAMS_REPORTING_CSV_OPTION,
  TEAMS_REPORTING_PDF_OPTION,
  P2P_DETAIL_REPORTING_CSV_OPTION,
  P2P_DETAIL_REPORTING_PDF_OPTION,
];

/**
 * Returns dropdown options for the Search UBT Projects page.
 * Includes team-level and project-level report exports.
 *
 * @returns Options array for the Search UBT Projects report dropdown.
 */
export const getSearchUBTProjectsOptions = (): DropdownOption[] => [
  TEAM_SUMMARY_OPTION,
  TEAM_DETAILS_OPTION,
  EXPORT_PIP_HISTORY_OPTION,
  EXPORT_PIP_DETAILS_OPTION,
  EXPORT_TEST_OF_CHANGE_OPTION,
  EXPORT_TEST_DATA_OPTION,
];

/**
 * Returns dropdown options for the My Teams page.
 * Includes all team-level, PIP-level, and roles exports available to the user.
 *
 * @returns Options array for the My Teams report dropdown.
 */
export const getMyTeamsOptions = (): DropdownOption[] => [
  TEAM_SUMMARY_OPTION,
  TEAM_DETAILS_OPTION,
  EXPORT_GL_DATA_OPTION,
  EXPORT_ROLES_OPTION,
  EXPORT_PIP_HISTORY_OPTION,
  EXPORT_PIP_DETAILS_OPTION,
  EXPORT_TEST_OF_CHANGE_OPTION,
  EXPORT_TEST_DATA_OPTION,
  EXPORT_UBT_DETAILS_OPTION,
];

/**
 * Returns dropdown options for the DetailsContainer "Team Reports & Exports" dropdown.
 * Provides team-level summary/details exports and PIP-level exports for the current team.
 *
 * @returns Options array for the Team Reports & Exports dropdown.
 */
export const getDetailsContainerTeamReportOptions = (): DropdownOption[] => [
  TEAM_SUMMARY_OPTION,
  TEAM_DETAILS_OPTION,
  EXPORT_PIP_HISTORY_OPTION,
  EXPORT_PIP_DETAILS_OPTION,
  EXPORT_TOC_PIP_OPTION,
];

/**
 * Returns dropdown options for the DetailsContainer "Project Reports & Exports" dropdown.
 * Shows project-specific print and export actions for the currently viewed project.
 *
 * @returns Options array for the Project Reports & Exports dropdown.
 */
export const getDetailsContainerProjectReportOptions = (): DropdownOption[] => [
   TEST_DATA_CHARTS_OPTION,
  PRINT_PROJECT_RECORDS_OPTION,
  PRINT_TEST_OF_CHANGE_OPTION,
  EXPORT_PROJECT_TEST_DATA_OPTION,
];
